import React, { useEffect, useRef, useState } from "react";
import { freight } from "../lib/freightApi.js";
import LiveListen, { primeAudio } from "./LiveListen.jsx";
import { TABS, inTab, MATCH, STATUS, summaryLine, callLabel, transferLabel, handoffLabel, fileText } from "./recruitingView.js";

/**
 * DRIVER RECRUITING (2026-10-25). Brian: "researching the carrier's existing/previous job
 * applications to see if there are any qualified drivers that were missed … reviewing job
 * applications as they come in to vet the driver and gather information." And on the hand-off:
 * "Try to transfer first if no one is available then email. Text only if the HR representative
 * has allowed it."
 *
 * The board's look comes in through `ui` — the dashboard's own primitives, passed down rather
 * than imported, so this file adds a screen without reshaping Dispatch.jsx a week before a demo.
 *
 * ⛔ WHAT THIS SCREEN NEVER CLAIMS. Alfred asks; he never checks a record and never decides.
 * "Looks like a fit" is a sort order against what the carrier said it hires for, and it is never
 * said to the applicant. Every label about a call says only what the platform recorded.
 */

const CDL_OPTIONS = [{ v: "A", label: "Class A" }, { v: "B", label: "Class B" }, { v: "C", label: "Class C" }, { v: "", label: "Any" }];
const ENDORSE_OPTIONS = [["H", "Hazmat"], ["N", "Tanker"], ["T", "Doubles/triples"], ["X", "Tanker + hazmat"]];
const errText = (e) => e?.body?.message || e?.body?.note || e?.message || "Something went wrong.";

export default function Recruiting({ ui, initialApplicantId = null }) {
  const { T } = ui;
  const [data, setData] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [tab, setTab] = useState("missed");
  const [openId, setOpenId] = useState(initialApplicantId ? Number(initialApplicantId) : null);
  const [err, setErr] = useState(null);

  const reload = async () => {
    try {
      const [r, list] = await Promise.all([freight.getRecruiting(), freight.listApplicants()]);
      setData(r); setApplicants(list); setErr(null);
    } catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { reload(); }, []);

  const open = applicants.find((a) => a.id === openId) || null;
  const shown = inTab(applicants, tab);
  const counts = Object.fromEntries(TABS.map((t) => [t.v, inTab(applicants, t.v).length]));

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6 }}>Recruiting</div>
        <div style={{ fontSize: 13, color: T.ink3, marginTop: 4, lineHeight: 1.5, maxWidth: 760 }}>
          Alfred calls your applicants, asks what a recruiter asks first, and puts the interested ones through to your
          recruiter — or emails them the call if nobody picks up. He never checks records and never decides who you hire.
        </div>
      </div>
      {err && <Banner T={T} tone="bad">{err}</Banner>}
      {data && <SettingsPanel ui={ui} data={data} onSaved={(settings) => setData({ ...data, settings })} />}
      {data && <ImportPanel ui={ui} consentText={data.importConsentText} onImported={reload} />}
      <ui.Panel>
        <ui.PanelHead title="Applicants"
          note={tab === "missed"
            ? "From your past applications: people who look like what you hire for and whom nobody ever reached."
            : "Everyone who applied, newest first."}
          right={<ui.Segmented value={tab} onChange={setTab} options={TABS.map((t) => ({ v: t.v, label: `${t.label}${counts[t.v] ? ` ${counts[t.v]}` : ""}` }))} />} />
        {shown.length === 0
          ? <div style={{ padding: "6px 20px 22px", fontSize: 13, color: T.ink3 }}>
              {applicants.length === 0 ? "No applicants yet. Import a spreadsheet of past applications, or share your application link." : "Nobody in this list."}
            </div>
          : shown.map((a) => (
            <ApplicantRow key={a.id} ui={ui} a={a} open={a.id === openId} onToggle={() => setOpenId(a.id === openId ? null : a.id)} />
          ))}
      </ui.Panel>
      {open && data && (
        <ApplicantDetail key={open.id} ui={ui} a={open} onboardingItems={data.onboardingItems || []}
          callsConfigured={data.settings.callsConfigured} onChanged={reload} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

function Banner({ T, tone, children }) {
  return (
    <div style={{ fontSize: 13, padding: "10px 14px", borderRadius: 8, lineHeight: 1.45,
      border: `1px solid ${tone === "bad" ? T.ink : T.hair}`, background: tone === "bad" ? "#fff" : T.wash }}>
      {children}
    </div>
  );
}

function Input({ T, label, value, onChange, placeholder, type = "text", width }) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: 12.5, color: T.ink2, width: width || "100%", minWidth: 0 }}>
      {label}
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        style={{ font: "inherit", fontSize: 13.5, color: T.ink, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "8px 10px", outline: "none", minWidth: 0 }} />
    </label>
  );
}

/* ── settings ──────────────────────────────────────────────────────────── */

export function SettingsPanel({ ui, data, onSaved }) {
  const { T } = ui;
  const s = data.settings;
  const [name, setName] = useState(s.recruiter.name || "");
  const [phone, setPhone] = useState(s.recruiter.phoneE164 || "");
  const [email, setEmail] = useState(s.recruiter.email || "");
  const [textOk, setTextOk] = useState(s.recruiter.textOk === true);
  const [cdl, setCdl] = useState(s.criteria.cdlClass || "");
  const [years, setYears] = useState(String((s.criteria.minMonths || 0) / 12));
  const [ends, setEnds] = useState(s.criteria.endorsements || []);
  const [states, setStates] = useState((s.criteria.states || []).join(", "));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(false);

  const save = async (patch) => {
    setBusy(true); setMsg(null);
    try { onSaved(await freight.saveRecruiting(patch)); setMsg({ ok: true, text: "Saved." }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const saveAll = () => save({
    recruiter: { name, phone, email, textOk },
    criteria: {
      cdlClass: cdl || null,
      minMonths: Math.max(0, Math.round((Number(years) || 0) * 12)),
      endorsements: ends,
      states: states.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean)
    }
  });
  const copy = async () => {
    try { await navigator.clipboard.writeText(s.applyUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { setCopied(false); }
  };

  return (
    <ui.Panel>
      <ui.PanelHead title="How Alfred recruits for you"
        note="Your recruiter takes the live hand-offs and gets the emails. What you hire for only sorts the list and tells Alfred what to ask." />
      <div style={{ padding: "0 20px 18px", display: "grid", gap: 18 }}>
        <section style={{ display: "grid", gap: 10 }}>
          <ui.Micro>Your recruiter</ui.Micro>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Input T={T} label="Name" value={name} onChange={setName} placeholder="Marcus Webb" width={200} />
            <Input T={T} label="Phone (for live transfers)" value={phone} onChange={setPhone} placeholder="(713) 555-0188" type="tel" width={190} />
            <Input T={T} label="Email (gets every screened call)" value={email} onChange={setEmail} placeholder="recruiting@yourco.com" type="email" width={250} />
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: T.ink2, lineHeight: 1.45, maxWidth: 640 }}>
            <input type="checkbox" checked={textOk} onChange={(e) => setTextOk(e.target.checked)} disabled={!phone} style={{ marginTop: 2 }} />
            <span>Text my recruiter too. <span style={{ color: T.ink3 }}>{s.textConsent} Texts are not connected yet: until they are, Alfred emails only.</span></span>
          </label>
          <div style={{ fontSize: 12.5, color: s.recruiter.transferError ? T.ink : T.ink3 }}>
            {s.recruiter.transferReady
              ? "Live transfer is ready: Alfred can put an interested applicant straight through to your recruiter."
              : s.recruiter.transferError
                ? `Live transfer is not set up: ${s.recruiter.transferError}`
                : s.live === false && s.recruiter.name && s.recruiter.phoneE164
                  ? "Live transfer is set up when you go live. Until then, Alfred emails your recruiter after each call."
                  : "Add a name and phone to let Alfred put applicants through live. Without one, he emails your recruiter after each call."}
          </div>
        </section>

        <section style={{ display: "grid", gap: 10 }}>
          <ui.Micro>What you hire for</ui.Micro>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ display: "grid", gap: 4, fontSize: 12.5, color: T.ink2 }}>
              CDL
              <select value={cdl} onChange={(e) => setCdl(e.target.value)} style={{ font: "inherit", fontSize: 13.5, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "8px 10px", background: "#fff" }}>
                {CDL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </label>
            <Input T={T} label="Years driving, at least" value={years} onChange={setYears} type="number" width={150} />
            <Input T={T} label="States you hire in (blank = anywhere)" value={states} onChange={setStates} placeholder="TX, LA" width={230} />
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13 }}>
            {ENDORSE_OPTIONS.map(([k, label]) => (
              <label key={k} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={ends.includes(k)} onChange={(e) => setEnds(e.target.checked ? [...ends, k] : ends.filter((x) => x !== k))} />
                {label}
              </label>
            ))}
          </div>
        </section>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <ui.Button onClick={saveAll} disabled={busy}>{busy ? "Saving…" : "Save"}</ui.Button>
          {msg && <span style={{ fontSize: 12.5, color: msg.ok ? T.ink3 : T.ink }}>{msg.text}</span>}
        </div>

        <section style={{ display: "grid", gap: 10, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 16 }}>
          <ui.Micro>New applications</ui.Micro>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <ui.Toggle on={s.autoCall} onChange={(v) => save({ autoCall: v })} />
            <div style={{ fontSize: 13, lineHeight: 1.45 }}>
              Call new applications automatically
              <div style={{ fontSize: 12.5, color: T.ink3 }}>Once, within about a minute, 8am–9pm where they are — and only if they ticked "call me" on the form.</div>
            </div>
          </div>
          {s.applyOpen === false
            ? <div style={{ fontSize: 12.5, color: T.ink3 }}>Your application link opens when you go live.</div>
            : s.applyUrl
            ? <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <code style={{ fontFamily: ui.mono, fontSize: 12.5, background: T.wash, borderRadius: 6, padding: "6px 9px", wordBreak: "break-all" }}>{s.applyUrl}</code>
                <ui.Button kind="quiet" size="sm" onClick={copy}>{copied ? "Copied" : "Copy link"}</ui.Button>
                <ui.Button kind="quiet" size="sm" onClick={async () => { try { onSaved(await freight.newApplyLink()); } catch (e) { setMsg({ ok: false, text: errText(e) }); } }}>New link</ui.Button>
              </div>
            : <div style={{ fontSize: 12.5, color: T.ink3 }}>The application link appears once the app knows its own address (FRONTEND_URL).</div>}
        </section>
      </div>
    </ui.Panel>
  );
}

/* ── import ────────────────────────────────────────────────────────────── */

export function ImportPanel({ ui, consentText, onImported }) {
  const { T } = ui;
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  const pick = async (f) => {
    setMsg(null); setPreview(null); setAttest(false);
    const text = await fileText(f);
    setCsv(text);
    if (!text.trim()) return;
    try { setPreview(await freight.previewApplicants(text)); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await freight.importApplicants(csv, attest);
      setMsg({ ok: true, text: `Imported ${r.imported} applicant${r.imported === 1 ? "" : "s"}.${r.errors?.length ? ` ${r.errors.length} line${r.errors.length === 1 ? "" : "s"} skipped.` : ""}` });
      setPreview(null); setCsv(""); if (fileRef.current) fileRef.current.value = "";
      onImported();
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };

  return (
    <ui.Panel>
      <ui.PanelHead title="Find the drivers you missed"
        note="Export past applications from wherever they live today (a spreadsheet, your applicant system, a job board) and drop the CSV here. Only name, phone, email, location, CDL, endorsements, experience, equipment, date and status are kept: never date of birth, SSN or licence number." />
      <div style={{ padding: "0 20px 18px", display: "grid", gap: 12 }}>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => pick(e.target.files?.[0])} style={{ fontSize: 13 }} />
        {preview && (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontSize: 13 }}>
              {preview.wouldImport} applicant{preview.wouldImport === 1 ? "" : "s"} found.
              {preview.errors?.length > 0 && <span style={{ color: T.ink3 }}> {preview.errors.length} line{preview.errors.length === 1 ? "" : "s"} will be skipped:</span>}
            </div>
            {preview.errors?.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: T.ink2 }}>
                {preview.errors.slice(0, 8).map((e, i) => <li key={i}>Line {e.line}: {e.message}</li>)}
              </ul>
            )}
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.45, maxWidth: 680 }}>
              <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} style={{ marginTop: 2 }} />
              <span>{consentText} <span style={{ color: T.ink3 }}>Tick this so Alfred may call them. Untick it and they import without call consent: you can still read the list.</span></span>
            </label>
            <div><ui.Button onClick={apply} disabled={busy || !preview.wouldImport}>{busy ? "Importing…" : `Import ${preview.wouldImport}`}</ui.Button></div>
          </div>
        )}
        {msg && <div style={{ fontSize: 12.5, color: msg.ok ? T.ink3 : T.ink }}>{msg.text}</div>}
      </div>
    </ui.Panel>
  );
}

/* ── the list ──────────────────────────────────────────────────────────── */

export function ApplicantRow({ ui, a, open, onToggle }) {
  const { T } = ui;
  const m = MATCH[a.match] || MATCH.check;
  const last = callLabel(a.lastCall);
  return (
    <button onClick={onToggle} className="alf-row" style={{ width: "100%", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", textAlign: "left",
      font: "inherit", padding: "12px 20px", background: open ? T.wash : "none", border: "none", borderTop: `1px solid ${T.hairSoft}`, cursor: "pointer" }}>
      <div style={{ flex: "1 1 220px", minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{a.name}</div>
        <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>{summaryLine(a) || "Nothing on file but a name and number"}</div>
      </div>
      <span style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 4, whiteSpace: "nowrap",
        background: m.fill ? T.ink : "transparent", color: m.fill ? "#fff" : m.muted ? T.ink3 : T.ink, border: `1px solid ${m.fill ? T.ink : T.hair}` }}>{m.label}</span>
      <span style={{ fontSize: 12.5, color: T.ink2, minWidth: 120 }}>{last || STATUS[a.status] || a.status}</span>
      {a.persona && <ui.Soft>AI persona</ui.Soft>}
      {!a.consent && a.status !== "do_not_contact" && <ui.Soft>No call consent</ui.Soft>}
      {a.status === "do_not_contact" && <ui.Tag solid>Do not contact</ui.Tag>}
    </button>
  );
}

/* ── one applicant ─────────────────────────────────────────────────────── */

export function ApplicantDetail({ ui, a, onboardingItems, callsConfigured, onChanged, onClose }) {
  const { T } = ui;
  const [calls, setCalls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showTx, setShowTx] = useState(null);
  // A call to an AI persona, heard live here: { callId } (null while the platform is being asked).
  const [live, setLive] = useState(null);
  const poll = useRef(null);

  const loadCalls = async () => { try { setCalls(await freight.listApplicantCalls(a.id)); } catch { /* the list still shows */ } };
  useEffect(() => { loadCalls(); return () => clearInterval(poll.current); }, [a.id]);

  // Nothing pushes into this app: after a call is placed, ask until it settles (6 min ceiling).
  const watch = (callId) => {
    clearInterval(poll.current);
    const until = Date.now() + 6 * 60_000;
    poll.current = setInterval(async () => {
      try {
        const c = await freight.getApplicantCall(callId);
        // The live panel closes when the call settles, never on this ceiling: a screen plus a
        // hand-off plus the recruiter talking can run past six minutes, and cutting the room's
        // audio mid-call is worse than a stale panel (found in review).
        const settled = !c || !["queued", "placed"].includes(c.status);
        if (settled || Date.now() > until) {
          clearInterval(poll.current); if (settled) setLive(null); await loadCalls(); onChanged();
        }
      } catch { clearInterval(poll.current); }
    }, 4000);
  };

  const act = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try { await fn(); if (okText) setMsg({ ok: true, text: okText }); await loadCalls(); onChanged(); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const callNow = (reason) => {
    // ⛔ BEFORE ANY AWAIT: this click is the gesture that lets the page make sound, and an AI
    // persona's call is heard here, live (LiveListen.jsx). The ring starts now.
    if (a.persona) { primeAudio(); setLive({ callId: null }); }
    return act(async () => {
      let r;
      try { r = await freight.callApplicant(a.id, reason); } catch (e) { setLive(null); throw e; }
      if (r?.call?.id) watch(r.call.id);
      setLive(r?.call?.persona && r.call.id ? { callId: r.call.id } : null);
    }, a.persona ? null : "Alfred is calling. This updates by itself when the call ends.");
  };

  const facts = [
    ["Phone", a.phoneE164], ["Email", a.email], ["Summary", summaryLine(a)],
    ["Equipment", (a.equipment || []).join(", ")], ["Applied", a.appliedAt ? new Date(a.appliedAt).toLocaleDateString() : null],
    ["Their system said", a.priorStatus], ["Source", a.source === "import" ? "Imported file" : a.source === "apply" ? "Application link" : a.source]
  ].filter(([, v]) => v);
  // Not while a call is already out: the server refuses a second one anyway (one open call each).
  const inFlight = a.status === "screening" || ["queued", "placed"].includes(a.lastCall?.status);
  const canCall = a.consent && a.status !== "do_not_contact" && callsConfigured && !inFlight;
  const consentNote = a.persona ? `An AI plays ${a.name.split(" ")[0]} for demonstrations, on a 555-01xx number no phone has. Call rings the AI, never a person, and you hear the call live here.`
    : !a.consent ? "No consent to automated calls on file, so Alfred will not call them."
    : a.source === "apply" ? `Agreed to automated calls on the application form${a.consentAt ? ` (${new Date(a.consentAt).toLocaleDateString()})` : ""}.`
      : a.source === "import" ? `You confirmed on import that they applied and gave this number to be contacted${a.consentAt ? ` (${new Date(a.consentAt).toLocaleDateString()})` : ""}.`
        : "Agreed to automated calls.";
  const ob = a.onboarding || {};

  return (
    <ui.Panel>
      <ui.PanelHead title={a.name} note={STATUS[a.status] || a.status}
        right={<ui.Button kind="quiet" size="sm" onClick={onClose}>Close</ui.Button>} />
      <div style={{ padding: "0 20px 18px", display: "grid", gap: 16 }}>
        <div style={{ display: "grid", gap: 2, maxWidth: 520 }}>
          {facts.map(([k, v]) => <ui.Field key={k} label={k} value={v} />)}
        </div>

        <div style={{ display: "grid", gap: 4 }}>
          <ui.Micro>Against what you hire for</ui.Micro>
          {(a.matchReasons || []).map((r, i) => (
            <div key={i} style={{ fontSize: 13, color: r.ok === false ? T.ink : T.ink2 }}>{r.ok === true ? "✓" : r.ok === false ? "✗" : "?"} {r.text}</div>
          ))}
          <div style={{ fontSize: 12, color: T.ink3 }}>A sort order, not a decision. Alfred never tells an applicant whether they fit.</div>
        </div>

        <div style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.45 }}>
          {consentNote}{inFlight && a.consent ? " Alfred is on a call with them now." : ""}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canCall && a.status !== "hired" && <>
            <ui.Button onClick={() => callNow(a.source === "import" ? "APPLICANT_REENGAGE" : "APPLICANT_SCREEN")} disabled={busy}>
              {a.source === "import" ? "Call: still looking?" : "Call to screen"}
            </ui.Button>
          </>}
          {!callsConfigured && <span style={{ fontSize: 12.5, color: T.ink3 }}>Calling is not connected on this account yet.</span>}
          {a.status !== "hired" && a.status !== "do_not_contact" &&
            <ui.Button kind="quiet" onClick={() => act(() => freight.hireApplicant(a.id), "Added to your drivers. Onboarding started below.")} disabled={busy}>Hire</ui.Button>}
          {!["not_now", "hired", "do_not_contact"].includes(a.status) &&
            <ui.Button kind="quiet" onClick={() => act(() => freight.updateApplicant(a.id, { status: "not_now" }))} disabled={busy}>Not now</ui.Button>}
          {a.status !== "do_not_contact" &&
            <ui.Button kind="quiet" onClick={() => act(() => freight.updateApplicant(a.id, { status: "do_not_contact" }))} disabled={busy}>Do not contact</ui.Button>}
        </div>
        {msg && <div style={{ fontSize: 12.5, color: msg.ok ? T.ink3 : T.ink }}>{msg.text}</div>}
        {live && (
          <div style={{ border: `1px solid ${T.ink}`, borderRadius: 8, padding: "10px 12px" }}>
            <LiveListen T={T} mono={ui.mono} callId={live.callId} name={a.name} listen={freight.listenApplicantCall} />
          </div>
        )}

        {a.status === "hired" && (
          <div style={{ display: "grid", gap: 6 }}>
            <ui.Micro>Onboarding</ui.Micro>
            <div style={{ fontSize: 12, color: T.ink3 }}>Done by you, with the driver, outside this app. Alfred tracks it; he never runs a check.</div>
            {onboardingItems.map((it) => (
              <label key={it.key} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                <input type="checkbox" checked={ob[it.key]?.done === true}
                  onChange={(e) => act(() => freight.setOnboarding(a.id, it.key, e.target.checked))} />
                {it.label}
              </label>
            ))}
          </div>
        )}

        <div style={{ display: "grid", gap: 8 }}>
          <ui.Micro>Calls</ui.Micro>
          {calls.length === 0 && <div style={{ fontSize: 12.5, color: T.ink3 }}>No calls yet.</div>}
          {calls.map((c) => (
            <div key={c.id} style={{ border: `1px solid ${T.hairSoft}`, borderRadius: 8, padding: "10px 12px", display: "grid", gap: 4 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>{callLabel(c)}</span>
                <span style={{ color: T.ink3 }}>{new Date(c.createdAt).toLocaleString()}</span>
                {c.reason === "APPLICANT_REENGAGE" && <ui.Soft>Still looking?</ui.Soft>}
                {c.persona && <ui.Soft>AI persona</ui.Soft>}
              </div>
              {transferLabel(c.transfer) && <div style={{ fontSize: 12.5 }}>{transferLabel(c.transfer)}</div>}
              {handoffLabel(c) && <div style={{ fontSize: 12.5, color: c.handoff === "emailed" ? T.ink2 : T.ink }}>
                {handoffLabel(c)}{c.handoffError ? ` — ${c.handoffError}` : ""}
              </div>}
              {c.status === "failed" && c.error && <div style={{ fontSize: 12.5, color: T.ink }}>Why: {c.error}</div>}
              {c.transcript && (
                <div>
                  <button onClick={() => setShowTx(showTx === c.id ? null : c.id)} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", padding: 0, color: T.ink, textDecoration: "underline", cursor: "pointer" }}>
                    {showTx === c.id ? "Hide transcript" : "Read the transcript"}
                  </button>
                  {showTx === c.id && <pre style={{ whiteSpace: "pre-wrap", fontFamily: ui.mono, fontSize: 12, background: T.wash, borderRadius: 6, padding: 10, marginTop: 6 }}>{c.transcript}</pre>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </ui.Panel>
  );
}
